import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Users, Target, Clock, Zap, Eye, Calendar, Play, MapPin, Hash, Flame, TrendingUp, Star } from 'lucide-react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../../config/firebase';

const FanDashboardPage = () => {
  const [liveMatches, setLiveMatches] = useState([]);
  const [allLiveMatches, setAllLiveMatches] = useState([]);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [allUpcomingMatches, setAllUpcomingMatches] = useState([]);
  const [completedMatches, setCompletedMatches] = useState([]);
  const [allCompletedMatches, setAllCompletedMatches] = useState([]);
  const [teams, setTeams] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('live'); // 'live', 'upcoming', or 'results'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableResultDates, setAvailableResultDates] = useState([]);
  const [selectedTime, setSelectedTime] = useState('all'); // 'all' or time slot
  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('all'); // 'all' or team ID
  const [availableTeams, setAvailableTeams] = useState([]);

  useEffect(() => {
    // Listen to all fixtures and filter in JavaScript
    const fixturesQuery = query(collection(db, 'fixtures'));

    const unsubscribeFixtures = onSnapshot(fixturesQuery, (snapshot) => {
      const allFixtures = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter live matches (exclude completed)
      console.log('All fixtures:', allFixtures.map(f => ({ id: f.id, status: f.status, homeTeam: f.homeTeam, awayTeam: f.awayTeam })));
      const live = allFixtures.filter(f => 
        (f.status === 'live' || f.status === 'paused') && 
        f.status !== 'completed'
      );
      console.log('Filtered live matches:', live.map(f => ({ id: f.id, status: f.status, homeTeam: f.homeTeam, awayTeam: f.awayTeam })));
      setAllLiveMatches(live);
      setLiveMatches(live);
      
      // Get all upcoming scheduled matches
      const allUpcoming = allFixtures.filter(f => f.status === 'scheduled');
      setAllUpcomingMatches(allUpcoming);
      
      // Get all completed matches
      const allCompleted = allFixtures.filter(f => f.status === 'completed');
      setAllCompletedMatches(allCompleted);
      
      // Get unique dates from upcoming matches
      const dates = [...new Set(allUpcoming.map(f => f.date))].filter(Boolean).sort();
      setAvailableDates(dates);
      
      // Get unique dates from completed matches
      const resultDates = [...new Set(allCompleted.map(f => f.date))].filter(Boolean).sort();
      setAvailableResultDates(resultDates);
      
      // Get unique time slots from upcoming and completed matches
      const upcomingTimes = [...new Set(allUpcoming.map(f => f.time).filter(Boolean))].sort();
      const completedTimes = [...new Set(allCompleted.map(f => f.time).filter(Boolean))].sort();
      const allTimes = [...new Set([...upcomingTimes, ...completedTimes])].sort();
      setAvailableTimes(allTimes);
      
      // Set default date if not set or not available
      if (!selectedDate || !dates.includes(selectedDate)) {
      const today = new Date().toISOString().split('T')[0];
        setSelectedDate(dates.includes(today) ? today : dates[0] || today);
      }
      
      setLoading(false);
    });

    // Fetch team data for logos
    const teamsQuery = query(collection(db, 'teams'));
    const unsubscribeTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = {};
      const teamsList = [];
      snapshot.docs.forEach(doc => {
        const teamData = { id: doc.id, ...doc.data() };
        teamsData[doc.id] = teamData;
        teamsList.push(teamData);
      });
      setTeams(teamsData);
      setAvailableTeams(teamsList.sort((a, b) => a.name.localeCompare(b.name)));
    });

    return () => {
      unsubscribeFixtures();
      unsubscribeTeams();
    };
  }, []);

  // Filter upcoming matches by selected date, time, and team
  useEffect(() => {
    if (selectedDate && allUpcomingMatches.length > 0) {
      let filtered = allUpcomingMatches.filter(f => f.date === selectedDate);
      
      // Apply time filter
      if (selectedTime !== 'all') {
        filtered = filtered.filter(f => f.time === selectedTime);
      }
      
      // Apply team filter
      if (selectedTeam !== 'all') {
        filtered = filtered.filter(f => 
          f.homeTeamId === selectedTeam || f.awayTeamId === selectedTeam
        );
      }
      
      setUpcomingMatches(filtered);
    }
  }, [selectedDate, selectedTime, selectedTeam, allUpcomingMatches]);

  // Filter completed matches by selected date, time, and team
  useEffect(() => {
    if (allCompletedMatches.length > 0) {
      let filtered = allCompletedMatches;
      
      // Apply date filter (only if not 'all')
      if (selectedDate && selectedDate !== 'all') {
        filtered = filtered.filter(f => f.date === selectedDate);
      }
      
      // Apply time filter
      if (selectedTime !== 'all') {
        filtered = filtered.filter(f => f.time === selectedTime);
      }
      
      // Apply team filter
      if (selectedTeam !== 'all') {
        filtered = filtered.filter(f => 
          f.homeTeamId === selectedTeam || f.awayTeamId === selectedTeam
        );
      }
      
      setCompletedMatches(filtered);
    }
  }, [selectedDate, selectedTime, selectedTeam, allCompletedMatches]);

  // Filter live matches by team
  useEffect(() => {
    if (selectedTeam !== 'all') {
      const filtered = allLiveMatches.filter(f => 
        f.homeTeamId === selectedTeam || f.awayTeamId === selectedTeam
      );
      setLiveMatches(filtered);
    } else {
      setLiveMatches(allLiveMatches);
    }
  }, [selectedTeam, allLiveMatches]);

  // Set default date to 'all' when switching to results tab
  useEffect(() => {
    if (activeTab === 'results') {
      setSelectedDate('all');
    }
  }, [activeTab]);

  const MatchCard = ({ match, isLive = false }) => {
    const homeTeam = teams[match.homeTeamId] || { name: match.homeTeam, logo: null };
    const awayTeam = teams[match.awayTeamId] || { name: match.awayTeam, logo: null };

    const getCurrentScore = () => {
      if (!match.sets) return { home: 0, away: 0 };
      const homeSetsWon = match.sets.filter(s => s.completed && s.homeScore > s.awayScore).length;
      const awaySetsWon = match.sets.filter(s => s.completed && s.awayScore > s.homeScore).length;
      return { home: homeSetsWon, away: awaySetsWon };
    };

    const score = getCurrentScore();
    const isCompleted = match.status === 'completed';
    const isUpcoming = match.status === 'scheduled';
    
    // Get current set info for live matches
    const currentSet = match.currentSet || 1;
    const currentSetData = match.sets?.find(s => s.set === currentSet);
    const gameScore = currentSetData ? {
      home: currentSetData.homeGames || 0,
      away: currentSetData.awayGames || 0
    } : { home: 0, away: 0 };

    // Determine winner for completed matches
    const winner = isCompleted ? (score.home > score.away ? 'home' : 'away') : null;

    return (
      <Link to={`/fan/match/${match.id}`} style={{ textDecoration: 'none' }}>
      <motion.div
        initial={false}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9 }}
          whileHover={{ y: -8, scale: 1.02 }}
        style={{
            background: isCompleted 
              ? 'linear-gradient(145deg, #f0fdf4 0%, #dcfce7 100%)'
              : 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)',
            borderRadius: '1.5rem',
          overflow: 'hidden',
            border: isLive 
              ? '2px solid #EF4444' 
              : isCompleted 
                ? '2px solid #10B981'
                : '2px solid #E5E7EB',
            boxShadow: isLive 
              ? '0 20px 40px rgba(239, 68, 68, 0.15), 0 0 0 1px rgba(239, 68, 68, 0.1)' 
              : isCompleted
                ? '0 20px 40px rgba(16, 185, 129, 0.15), 0 0 0 1px rgba(16, 185, 129, 0.1)'
                : '0 10px 30px rgba(0, 0, 0, 0.08)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative'
          }}
        >
          {/* Match Status Indicator */}
          {isLive && (
            <motion.div
              animate={{ 
                opacity: [0.5, 1, 0.5],
                scale: [0.95, 1.05, 0.95]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                background: '#EF4444',
                color: 'white',
                padding: '0.5rem 1rem',
                borderRadius: '2rem',
                fontSize: '0.75rem',
                fontWeight: '800',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                zIndex: 10,
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
              }}
            >
              <Flame size={14} />
              {match.status === 'live' ? 'LIVE NOW' : 'PAUSED'}
            </motion.div>
          )}

          {/* Completed Match Indicator */}
          {isCompleted && (
          <div style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '2rem',
              fontSize: '0.75rem',
              fontWeight: '800',
            display: 'flex',
            alignItems: 'center',
              gap: '0.5rem',
              zIndex: 10,
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
          }}>
              <Trophy size={14} />
              COMPLETED
            </div>
          )}

          {match.isPlayoff && (
            <div style={{
              position: 'absolute',
              top: isLive || isCompleted ? '3.25rem' : '1rem',
              right: '1rem',
              background: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
              color: '#7C2D12',
              padding: '0.35rem 0.85rem',
              borderRadius: '9999px',
              fontSize: '0.7rem',
              fontWeight: '800',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(124, 45, 18, 0.3)'
            }}>
              Playoff · {match.playoffRound || 'TBD'}
            </div>
          )}

          {/* Court & Match Info Header */}
          <div style={{
            background: isLive 
              ? 'linear-gradient(135deg, #0F3A2A 0%, #1C563D 100%)' 
              : isCompleted
                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                : 'linear-gradient(135deg, #064E3B 0%, #065F46 100%)',
            padding: '1rem 1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.875rem', fontWeight: '600' }}>
              <MapPin size={16} style={{ color: '#FCD34D' }} />
              <span>{match.court || 'Court TBD'}</span>
            </div>
              {match.matchNumber && (
              <div style={{ 
                background: 'rgba(252, 211, 77, 0.2)',
                color: '#FCD34D',
                padding: '0.375rem 0.875rem',
                borderRadius: '1rem',
                fontSize: '0.75rem',
                fontWeight: '700',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                  <Hash size={14} />
                Match {match.matchNumber}
              </div>
              )}
          </div>
                      
          {/* Main Content */}
          <div style={{ padding: '2rem 1.5rem' }}>
          {/* Teams Display */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              gap: '2rem',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              {/* Home Team */}
                <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '1rem',
                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(16, 185, 129, 0.3)',
                  border: '3px solid white',
                  position: 'relative'
                }}>
                  {homeTeam.logo ? (
                    <img 
                      src={homeTeam.logo} 
                      alt={homeTeam.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <Trophy size={40} style={{ color: 'white' }} />
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: winner === 'home' ? '900' : '800', 
                    color: winner === 'home' ? '#10B981' : '#0F3A2A', 
                    margin: '0 0 0.25rem 0',
                    lineHeight: 1.2,
                    textShadow: winner === 'home' ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none'
                  }}>
                    {homeTeam.name}
                    {winner === 'home' && ' 🏆'}
                  </h3>
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: '#6B7280', 
                    margin: 0,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Home
                  </p>
                </div>
                {isLive && (
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: '900', 
                    color: '#10B981',
                    lineHeight: 1
                  }}>
                    {score.home}
                  </div>
                )}
              </div>

              {/* VS / Score */}
              <div style={{ 
                textAlign: 'center',
                padding: '1rem',
                background: 'linear-gradient(135deg, #F0FDF4 0%, #D1FAE5 100%)',
                borderRadius: '1rem',
                minWidth: '100px'
              }}>
                {isLive ? (
                  <>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: '700', 
                      color: '#065F46',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Set {currentSet}
                </div>
                    <div style={{ 
                      fontSize: '1.75rem', 
                      fontWeight: '900', 
                      color: '#0F3A2A',
                      lineHeight: 1
                    }}>
                      {gameScore.home}-{gameScore.away}
                </div>
                  </>
                ) : isCompleted ? (
                  <>
                    <div style={{ 
                      fontSize: '0.75rem', 
                      fontWeight: '700', 
                      color: '#059669',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Final Score
                    </div>
                    <div style={{ 
                      fontSize: '2rem', 
                      fontWeight: '900', 
                      color: '#10B981',
                      lineHeight: 1
                    }}>
                      {score.home}-{score.away}
                    </div>
                  </>
                ) : (
                  <div style={{ 
                    fontSize: '2rem', 
                    fontWeight: '900',
                    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    VS
                  </div>
                )}
              </div>

              {/* Away Team */}
                <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: '1rem'
              }}>
                <div style={{ 
                  width: '80px', 
                  height: '80px', 
                  borderRadius: '1rem',
                  background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.3)',
                  border: '3px solid white'
                }}>
                  {awayTeam.logo ? (
                    <img 
                      src={awayTeam.logo} 
                      alt={awayTeam.name} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                    />
                  ) : (
                    <Trophy size={40} style={{ color: 'white' }} />
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h3 style={{ 
                    fontSize: '1.125rem', 
                    fontWeight: winner === 'away' ? '900' : '800', 
                    color: winner === 'away' ? '#10B981' : '#0F3A2A', 
                    margin: '0 0 0.25rem 0',
                    lineHeight: 1.2,
                    textShadow: winner === 'away' ? '0 2px 4px rgba(16, 185, 129, 0.3)' : 'none'
                  }}>
                    {awayTeam.name}
                    {winner === 'away' && ' 🏆'}
                  </h3>
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: '#6B7280', 
                    margin: 0,
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Away
                  </p>
                </div>
                {isLive && (
                  <div style={{ 
                    fontSize: '2.5rem', 
                    fontWeight: '900', 
                    color: '#3B82F6',
                    lineHeight: 1
                  }}>
                    {score.away}
                  </div>
                )}
              </div>
            </div>

            {/* Player Pairings */}
            {match.selectedPlayers && (
              <div style={{ 
                marginTop: '1.5rem',
                padding: '1.25rem',
                background: 'linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%)',
                borderRadius: '1rem',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{ 
                  fontSize: '0.75rem', 
                  fontWeight: '700', 
                  color: '#6B7280', 
                  marginBottom: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Player Pairings
                </div>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr auto 1fr', 
                  gap: '1rem',
                  alignItems: 'center'
                }}>
                  {/* Home Team Pairing */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '700', 
                      color: '#0F3A2A',
                      lineHeight: 1.4
                    }}>
                      {match.selectedPlayers.home?.player1?.name || 'Player 1'}
                      <br />
                      {match.selectedPlayers.home?.player2?.name || 'Player 2'}
                    </div>
                  </div>
                  
                  <div style={{ color: '#D1D5DB', fontWeight: '900' }}>|</div>
                  
                  {/* Away Team Pairing */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      fontWeight: '700', 
                      color: '#0F3A2A',
                      lineHeight: 1.4
                    }}>
                      {match.selectedPlayers.away?.player1?.name || 'Player 1'}
                      <br />
                      {match.selectedPlayers.away?.player2?.name || 'Player 2'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Set Scores - Show for Live and Completed matches */}
            {(isLive || isCompleted) && match.sets && match.sets.length > 0 && (
              <div style={{ 
                marginTop: '1.5rem',
                padding: '1.25rem',
                background: isLive 
                  ? 'linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)'
                  : 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                borderRadius: '1rem',
                border: isLive 
                  ? '1px solid #FECACA'
                  : '1px solid #BBF7D0'
              }}>
                    <div style={{ 
                      fontSize: '0.75rem', 
                  fontWeight: '700', 
                  color: isLive ? '#DC2626' : '#059669', 
                  marginBottom: '1rem',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  Set Scores
                    </div>
                    <div style={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  {match.sets.map((set, index) => {
                    const isCurrentSet = match.currentSet === index + 1;
                    const isCompleted = set.completed;
                    const homeScore = set.homeScore || set.homeGames || 0;
                    const awayScore = set.awayScore || set.awayGames || 0;
                    
                    return (
                      <div
                        key={index}
                        style={{
                          background: isCurrentSet 
                            ? (isLive 
                                ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
                                : 'linear-gradient(135deg, #10B981 0%, #059669 100%)')
                            : isCompleted 
                              ? 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
                              : 'linear-gradient(135deg, #E5E7EB 0%, #D1D5DB 100%)',
                          color: isCurrentSet || isCompleted ? 'white' : '#374151',
                          padding: '0.5rem 0.75rem',
                          borderRadius: '0.5rem',
                          textAlign: 'center',
                          minWidth: '60px',
                          border: isCurrentSet 
                            ? (isLive ? '2px solid #EF4444' : '2px solid #10B981')
                            : '2px solid transparent',
                          boxShadow: isCurrentSet 
                            ? (isLive 
                                ? '0 4px 12px rgba(239, 68, 68, 0.3)'
                                : '0 4px 12px rgba(16, 185, 129, 0.3)')
                            : '0 2px 4px rgba(0, 0, 0, 0.1)',
                          position: 'relative'
                        }}
                      >
                        <div style={{
                          fontSize: '0.625rem',
                          fontWeight: '600',
                          marginBottom: '0.25rem',
                          opacity: 0.8
                        }}>
                          Set {index + 1}
                          {isCurrentSet && ' ▶'}
                          {isCompleted && ' ✓'}
                    </div>
                        <div style={{
                          fontSize: '1rem',
                          fontWeight: '800',
                          lineHeight: 1
                        }}>
                          {homeScore}-{awayScore}
                  </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Match Metadata */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '1.5rem',
              marginTop: '1.5rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid #E5E7EB',
              fontSize: '0.875rem',
              color: '#6B7280',
              fontWeight: '600'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Calendar size={16} style={{ color: '#10B981' }} />
                  <span>{new Date(match.date).toLocaleDateString()}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Clock size={16} style={{ color: '#10B981' }} />
                  <span>{match.time || 'TBD'}</span>
                </div>
              </div>
          </div>

          {/* Watch Live CTA */}
              {isLive && (
                <div style={{ 
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              padding: '1rem',
              textAlign: 'center',
              color: 'white',
              fontWeight: '800',
              fontSize: '0.875rem',
              textTransform: 'uppercase',
              letterSpacing: '1px',
                  display: 'flex', 
                  alignItems: 'center', 
              justifyContent: 'center',
              gap: '0.75rem'
            }}>
              <Play size={18} fill="white" />
              Watch Live Now
                </div>
              )}
      </motion.div>
        </Link>
    );
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: '#FFFFFF',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          style={{ textAlign: 'center' }}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <Trophy size={64} style={{ margin: '0 auto 1rem', color: '#10B981' }} />
          </motion.div>
          <p style={{ color: '#4B5563', fontSize: '1.25rem', fontWeight: '600' }}>Loading matches...</p>
        </motion.div>
      </div>
    );
  }

  const displayMatches = activeTab === 'live' ? liveMatches : activeTab === 'upcoming' ? upcomingMatches : completedMatches;

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decorative Background Elements */}
      <div style={{
        position: 'absolute',
        top: '-10%',
        right: '-5%',
        width: '40%',
        height: '40%',
        background: 'radial-gradient(circle, rgba(252, 211, 77, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>
      <div style={{
        position: 'absolute',
        bottom: '-10%',
        left: '-5%',
        width: '40%',
        height: '40%',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      }}></div>

      <div className="container mx-auto px-4" style={{ paddingTop: 'clamp(2rem, 5vw, 3rem)', paddingBottom: '3rem', position: 'relative', zIndex: 1 }}>
        {/* Stunning Header with Branding */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          style={{ 
            textAlign: 'center', 
            marginBottom: 'clamp(2rem, 5vw, 3rem)',
            position: 'relative'
          }}
        >
          {/* Logo Section */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'clamp(1rem, 3vw, 2rem)',
              marginBottom: 'clamp(1.5rem, 4vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <img 
              src="/offcourt_logo.png" 
              alt="OffCourt Logo" 
              style={{ 
                height: 'clamp(50px, 10vw, 80px)',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }} 
            />
            <div style={{
              height: 'clamp(40px, 8vw, 60px)',
              width: '2px',
              background: 'linear-gradient(180deg, transparent, #FCD34D, transparent)'
            }}></div>
            <img 
              src="/thinkincode-emblem.png" 
              alt="ThinkinCode Emblem" 
              style={{ 
                height: 'clamp(40px, 8vw, 60px)',
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
              }} 
            />
          </motion.div>

          {/* Main Title */}
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
        >
          <h1 style={{ 
              fontSize: 'clamp(2rem, 7vw, 4rem)', 
              fontWeight: '900', 
              background: 'linear-gradient(135deg, #0F3A2A 0%, #1C563D 50%, #10B981 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: 'clamp(0.5rem, 2vw, 1rem)',
              letterSpacing: '-0.02em',
              lineHeight: 1.1
            }}>
              OffCourt Live
          </h1>
          <p style={{ 
              fontSize: 'clamp(1rem, 3vw, 1.5rem)', 
              color: '#4B5563', 
              fontWeight: '600',
              letterSpacing: '0.5px'
            }}>
              Professional Padel League
          </p>
        </motion.div>

          {/* Stats Bar */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 'clamp(1rem, 3vw, 2rem)',
              marginTop: 'clamp(1.5rem, 4vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <div style={{ 
              background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
              border: '2px solid #EF4444',
              borderRadius: '1rem',
              padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.25rem, 3vw, 2rem)',
              boxShadow: '0 4px 12px rgba(239, 68, 68, 0.2)'
            }}>
              <div style={{ 
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
                fontWeight: '900', 
                color: '#DC2626',
                lineHeight: 1
              }}>
                {liveMatches.length}
              </div>
              <div style={{ 
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', 
                color: '#991B1B',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginTop: '0.25rem'
              }}>
                Live Now
              </div>
            </div>
            <div style={{ 
              background: 'linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%)',
              border: '2px solid #3B82F6',
                borderRadius: '1rem',
              padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1.25rem, 3vw, 2rem)',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)'
            }}>
              <div style={{ 
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
                fontWeight: '900', 
                color: '#2563EB',
                lineHeight: 1
              }}>
                {upcomingMatches.length}
              </div>
              <div style={{ 
                fontSize: 'clamp(0.75rem, 2vw, 0.875rem)', 
                color: '#1E40AF',
                fontWeight: '700',
                textTransform: 'uppercase',
                letterSpacing: '1px',
                marginTop: '0.25rem'
              }}>
                Upcoming
            </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Tab Navigation */}
          <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: 'clamp(2rem, 5vw, 3rem)',
            flexWrap: 'wrap'
          }}
        >
          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('live')}
            style={{
              background: activeTab === 'live' 
                ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)' 
                : 'white',
              color: activeTab === 'live' ? 'white' : '#DC2626',
              border: '2px solid #EF4444',
              borderRadius: '1rem',
              padding: 'clamp(0.875rem, 2.5vw, 1.125rem) clamp(1.5rem, 4vw, 2.5rem)',
              fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.75rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: activeTab === 'live' 
                ? '0 10px 30px rgba(239, 68, 68, 0.3)' 
                : '0 4px 12px rgba(239, 68, 68, 0.15)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <Flame size={20} />
            Live Matches
            {liveMatches.length > 0 && (
              <span style={{
                background: activeTab === 'live' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(239, 68, 68, 0.15)',
                borderRadius: '1rem',
                padding: '0.25rem 0.75rem',
                fontSize: '0.875rem',
                fontWeight: '900'
              }}>
                {liveMatches.length}
              </span>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('upcoming')}
            style={{
              background: activeTab === 'upcoming' 
                ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)' 
                : 'white',
              color: activeTab === 'upcoming' ? 'white' : '#2563EB',
              border: '2px solid #3B82F6',
              borderRadius: '1rem',
              padding: 'clamp(0.875rem, 2.5vw, 1.125rem) clamp(1.5rem, 4vw, 2.5rem)',
              fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: activeTab === 'upcoming' 
                ? '0 10px 30px rgba(59, 130, 246, 0.3)' 
                : '0 4px 12px rgba(59, 130, 246, 0.15)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <Clock size={20} />
            Upcoming
        {upcomingMatches.length > 0 && (
              <span style={{
                background: activeTab === 'upcoming' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(59, 130, 246, 0.15)',
                borderRadius: '1rem',
                padding: '0.25rem 0.75rem',
                fontSize: '0.875rem',
                fontWeight: '900'
              }}>
                {upcomingMatches.length}
              </span>
            )}
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setActiveTab('results')}
            style={{
              background: activeTab === 'results' 
                ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)' 
                : 'white',
              color: activeTab === 'results' ? 'white' : '#10B981',
              border: '2px solid #10B981',
              borderRadius: '1rem',
              padding: 'clamp(0.875rem, 2.5vw, 1.125rem) clamp(1.5rem, 4vw, 2.5rem)',
              fontSize: 'clamp(0.875rem, 2vw, 1.125rem)',
              fontWeight: '800',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxShadow: activeTab === 'results' 
                ? '0 10px 30px rgba(16, 185, 129, 0.3)' 
                : '0 4px 12px rgba(16, 185, 129, 0.15)',
              textTransform: 'uppercase',
              letterSpacing: '1px'
            }}
          >
            <Trophy size={20} />
            Results
            {completedMatches.length > 0 && (
              <span style={{
                background: activeTab === 'results' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(16, 185, 129, 0.15)',
                borderRadius: '1rem',
                padding: '0.25rem 0.75rem',
                fontSize: '0.875rem',
                fontWeight: '900'
              }}>
                {completedMatches.length}
              </span>
            )}
          </motion.button>
        </motion.div>

        {/* Team Filter - Available for all tabs */}
        {availableTeams.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <span style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '700',
              color: '#4B5563',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Users size={18} style={{ color: '#6B7280' }} />
              Filter by Team:
            </span>
            <div style={{ 
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center',
              maxWidth: '100%'
            }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedTeam('all')}
                style={{
                  background: selectedTeam === 'all' 
                    ? 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
                    : 'white',
                  color: selectedTeam === 'all' ? 'white' : '#6B7280',
                  border: '2px solid #6B7280',
                  borderRadius: '0.75rem',
                  padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: selectedTeam === 'all' 
                    ? '0 4px 12px rgba(107, 114, 128, 0.3)'
                    : '0 2px 6px rgba(107, 114, 128, 0.1)',
                  whiteSpace: 'nowrap'
                }}
              >
                All Teams
              </motion.button>
              {availableTeams.slice(0, 8).map(team => (
                <motion.button
                  key={team.id}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedTeam(team.id)}
                  style={{
                    background: selectedTeam === team.id 
                      ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                      : 'white',
                    color: selectedTeam === team.id ? 'white' : '#10B981',
                    border: '2px solid #10B981',
                    borderRadius: '0.75rem',
                    padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                    fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    boxShadow: selectedTeam === team.id 
                      ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                      : '0 2px 6px rgba(16, 185, 129, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {team.logo && (
                    <img 
                      src={team.logo} 
                      alt={team.name}
                      style={{ 
                        width: '20px', 
                        height: '20px', 
                        objectFit: 'cover',
                        borderRadius: '50%',
                        border: '1px solid rgba(255, 255, 255, 0.3)'
                      }} 
                    />
                  )}
                  {team.name}
                </motion.button>
              ))}
              {availableTeams.length > 8 && (
                <div style={{
                  background: 'rgba(107, 114, 128, 0.1)',
                  color: '#6B7280',
                  padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                  borderRadius: '0.75rem',
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  fontWeight: '600',
                  border: '2px solid rgba(107, 114, 128, 0.2)'
                }}>
                  +{availableTeams.length - 8} more
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Date Filter for Upcoming Matches */}
        {activeTab === 'upcoming' && availableDates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <span style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '700',
              color: '#4B5563',
              display: 'flex', 
              alignItems: 'center', 
              gap: '0.5rem'
            }}>
              <Calendar size={18} style={{ color: '#3B82F6' }} />
              Select Date:
            </span>
              <div style={{ 
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {availableDates.map(date => {
                const dateObj = new Date(date + 'T00:00:00');
                const isToday = date === new Date().toISOString().split('T')[0];
                const isSelected = date === selectedDate;
                
                return (
                  <motion.button
                    key={date}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedDate(date)}
                    style={{
                      background: isSelected 
                        ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
                        : 'white',
                      color: isSelected ? 'white' : '#2563EB',
                      border: '2px solid #3B82F6',
                      borderRadius: '0.75rem',
                      padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                      fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      boxShadow: isSelected 
                        ? '0 4px 12px rgba(59, 130, 246, 0.3)'
                        : '0 2px 6px rgba(59, 130, 246, 0.1)',
                      position: 'relative'
                    }}
                  >
                    {isToday && (
                      <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: '#10B981',
                color: 'white',
                borderRadius: '1rem',
                        padding: '0.125rem 0.5rem',
                        fontSize: '0.625rem',
                        fontWeight: '800'
                      }}>
                        TODAY
                      </span>
                    )}
                    {dateObj.toLocaleDateString('en-US', { 
                      month: 'short', 
                      day: 'numeric',
                      year: dateObj.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
                    })}
                  </motion.button>
                );
              })}
              </div>
          </motion.div>
        )}

        {/* Time Filter for Upcoming and Results */}
        {(activeTab === 'upcoming' || activeTab === 'results') && availableTimes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <span style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '700',
              color: '#4B5563',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Clock size={18} style={{ color: '#6B7280' }} />
              Filter by Time:
            </span>
              <div style={{ 
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedTime('all')}
                style={{
                  background: selectedTime === 'all' 
                    ? 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
                    : 'white',
                  color: selectedTime === 'all' ? 'white' : '#6B7280',
                  border: '2px solid #6B7280',
                  borderRadius: '0.75rem',
                  padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: selectedTime === 'all' 
                    ? '0 4px 12px rgba(107, 114, 128, 0.3)'
                    : '0 2px 6px rgba(107, 114, 128, 0.1)',
                  whiteSpace: 'nowrap'
                }}
              >
                All Times
              </motion.button>
              {availableTimes.map(time => {
                const isSelected = selectedTime === time;
                const timeColor = activeTab === 'upcoming' ? '#3B82F6' : '#10B981';
                
                return (
                  <motion.button
                    key={time}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedTime(time)}
                    style={{
                      background: isSelected 
                        ? `linear-gradient(135deg, ${timeColor} 0%, ${timeColor === '#3B82F6' ? '#2563EB' : '#059669'} 100%)`
                        : 'white',
                      color: isSelected ? 'white' : timeColor,
                      border: `2px solid ${timeColor}`,
                      borderRadius: '0.75rem',
                      padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                      fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      boxShadow: isSelected 
                        ? `0 4px 12px ${timeColor}40`
                        : `0 2px 6px ${timeColor}20`,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {time}
                  </motion.button>
                );
              })}
              </div>
          </motion.div>
        )}

        {/* Date Filter for Results */}
        {activeTab === 'results' && availableResultDates.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.75rem',
              marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
              flexWrap: 'wrap'
            }}
          >
            <span style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '700',
              color: '#4B5563',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <Calendar size={18} style={{ color: '#10B981' }} />
              Select Date:
            </span>
            <div style={{ 
              display: 'flex',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {/* All Dates Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setSelectedDate('all')}
                style={{
                  background: selectedDate === 'all' 
                    ? 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)'
                    : 'white',
                  color: selectedDate === 'all' ? 'white' : '#6B7280',
                  border: '2px solid #6B7280',
                  borderRadius: '0.75rem',
                  padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                  fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  boxShadow: selectedDate === 'all' 
                    ? '0 4px 12px rgba(107, 114, 128, 0.3)'
                    : '0 2px 6px rgba(107, 114, 128, 0.1)',
                  whiteSpace: 'nowrap'
                }}
              >
                All Dates
              </motion.button>
              
              {availableResultDates.map(date => {
                const dateObj = new Date(date + 'T00:00:00');
                const isToday = date === new Date().toISOString().split('T')[0];
                const isSelected = date === selectedDate;
                
                return (
                  <motion.button
                    key={date}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setSelectedDate(date)}
                    style={{
                      background: isSelected 
                        ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
                        : 'white',
                      color: isSelected ? 'white' : '#10B981',
                      border: '2px solid #10B981',
                      borderRadius: '0.75rem',
                      padding: 'clamp(0.5rem, 1.5vw, 0.75rem) clamp(1rem, 2.5vw, 1.5rem)',
                      fontSize: 'clamp(0.75rem, 1.75vw, 0.875rem)',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s',
                      boxShadow: isSelected 
                        ? '0 4px 12px rgba(16, 185, 129, 0.3)'
                        : '0 2px 6px rgba(16, 185, 129, 0.1)',
                      position: 'relative'
                    }}
                  >
                    {isToday && (
                      <span style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                        color: 'white',
                        fontSize: '0.625rem',
                        fontWeight: '800',
                        padding: '0.125rem 0.375rem',
                        borderRadius: '0.5rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        boxShadow: '0 2px 4px rgba(245, 158, 11, 0.3)'
                      }}>
                        Today
                      </span>
                    )}
                    {dateObj.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Matches Grid */}
        <AnimatePresence mode="wait" initial={false}>
          {displayMatches.length > 0 ? (
          <motion.div
              key={activeTab}
            initial={false}
            animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 450px), 1fr))', 
                gap: 'clamp(1.5rem, 3vw, 2rem)',
                marginBottom: 'clamp(2rem, 5vw, 3rem)'
              }}
            >
              {displayMatches.map((match) => (
                <MatchCard 
                  key={match.id} 
                  match={match} 
                  isLive={activeTab === 'live'}
                />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.3 }}
              style={{ 
                textAlign: 'center', 
                padding: 'clamp(3rem, 8vw, 6rem) clamp(1rem, 3vw, 2rem)',
                background: 'linear-gradient(145deg, #F9FAFB 0%, #F3F4F6 100%)',
                borderRadius: '2rem',
                border: '2px dashed #D1D5DB',
                boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.05)'
              }}
            >
              <Trophy 
                size={window.innerWidth < 640 ? 64 : 96} 
                style={{ 
                  color: '#D1D5DB', 
                  margin: '0 auto clamp(1.5rem, 4vw, 2rem)',
                  display: 'block'
                }} 
              />
            <h3 style={{ 
                fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', 
                fontWeight: '900', 
                color: '#374151', 
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                letterSpacing: '-0.01em'
              }}>
                {activeTab === 'live' 
                  ? (selectedTeam !== 'all' ? 'No Live Matches for Selected Team' : 'No Live Matches')
                  : activeTab === 'upcoming' 
                    ? (selectedTeam !== 'all' || selectedTime !== 'all' ? 'No Upcoming Matches for Selected Filters' : 'No Upcoming Matches')
                    : (selectedTeam !== 'all' || selectedTime !== 'all' ? 'No Results for Selected Filters' : 'No Results Available')}
            </h3>
            <p style={{ 
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', 
                color: '#6B7280',
                maxWidth: '500px',
                margin: '0 auto'
              }}>
                {activeTab === 'live' 
                  ? (selectedTeam !== 'all' ? 'Try selecting a different team or check back later!' : 'Check back soon for live action!')
                  : activeTab === 'upcoming'
                    ? (selectedTeam !== 'all' || selectedTime !== 'all' ? 'Try selecting different filters, team, date, or time!' : 'No scheduled matches at this time.')
                    : (selectedTeam !== 'all' || selectedTime !== 'all' ? 'Try selecting different filters, team, date, or time!' : 'No completed matches found for this date.')}
            </p>
          </motion.div>
        )}
        </AnimatePresence>

        {/* Premium Footer with Branding */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          style={{
            background: 'linear-gradient(135deg, #0F3A2A 0%, #1C563D 100%)',
            borderRadius: '1.5rem',
            padding: 'clamp(1.5rem, 4vw, 2.5rem)',
            marginTop: 'clamp(3rem, 6vw, 4rem)',
            border: '2px solid rgba(16, 185, 129, 0.2)',
            boxShadow: '0 8px 24px rgba(15, 58, 42, 0.3)',
            textAlign: 'center'
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(0.75rem, 2vw, 1rem)',
            flexWrap: 'wrap',
            marginBottom: 'clamp(0.75rem, 2vw, 1rem)'
          }}>
            <Trophy size={24} style={{ color: '#FCD34D' }} />
            <span style={{
              fontSize: 'clamp(0.875rem, 2vw, 1rem)',
              fontWeight: '700',
              color: 'white',
              letterSpacing: '0.5px'
            }}>
              OffCourt Official League
            </span>
          </div>
          <div style={{
            fontSize: 'clamp(0.875rem, 2vw, 1rem)',
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: '600'
          }}>
            Powered by{' '}
              <a
                href="https://thinkincode.co.za"
                target="_blank"
                rel="noopener noreferrer"
              style={{
                color: '#FCD34D',
                textDecoration: 'none',
                fontSize: '1.125rem',
                fontWeight: '600',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.3s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#FBBF24';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#FCD34D';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>thinkinCode(</span>
                <img 
                  src="/thinkincode-emblem.png" 
                  alt="ThinkinCode Emblem" 
                  style={{ 
                  width: '35px', 
                  height: '35px',
                    objectFit: 'contain',
                    verticalAlign: 'middle'
                  }} 
                />
              <span style={{ fontFamily: 'Poppins, sans-serif', fontWeight: '600' }}>);</span>
              </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FanDashboardPage;